import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import './AdminPages.css';

const BlogManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/admin/blog');
      setPosts(response.data || []);
    } catch (err) {
      setError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (postData) => {
    try {
      const dataWithAuthor = { ...postData, author_id: user.id };
      await api.post('/admin/blog', dataWithAuthor);
      setSuccess('Blog post created!');
      setShowForm(false);
      fetchPosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to create blog post');
    }
  };

  const handleUpdate = async (id, postData) => {
    try {
      await api.put(`/admin/blog/${id}`, postData);
      setSuccess('Blog post updated!');
      setEditingPost(null);
      fetchPosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update blog post');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;

    try {
      await api.delete(`/admin/blog/${id}`);
      setSuccess('Blog post deleted!');
      fetchPosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete blog post');
    }
  };

  const handlePublish = async (id) => {
    try {
      await api.post(`/admin/blog/${id}/publish`);
      setSuccess('Blog post published!');
      fetchPosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to publish blog post');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Blog Posts Manager</h1>
          <div className="button-group">
            <button onClick={() => navigate('/admin')} className="btn btn-secondary">
              Back to Dashboard
            </button>
            <button onClick={() => setShowForm(true)} className="btn btn-primary">
              + New Blog Post
            </button>
          </div>
        </div>

        {success && <div className="success">{success}</div>}
        {error && <div className="error">{error}</div>}

        {posts.length === 0 ? (
          <div className="card">
            <p style={{ textAlign: 'center', color: 'var(--secondary-color)', padding: '40px' }}>
              No blog posts yet. Create your first post!
            </p>
          </div>
        ) : (
          <div className="admin-table">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Published Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id}>
                    <td>
                      <strong>{post.title}</strong>
                      {post.featured_image_url && (
                        <div style={{ marginTop: '5px' }}>
                          <img
                            src={post.featured_image_url}
                            alt={post.title}
                            style={{ width: '80px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        </div>
                      )}
                    </td>
                    <td>
                      <code style={{ fontSize: '0.85rem' }}>{post.slug}</code>
                    </td>
                    <td>
                      <span className={`status-badge ${post.is_published ? 'status-published' : 'status-draft'}`}>
                        {post.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>
                      {post.published_at ? new Date(post.published_at).toLocaleDateString() : 'Not published'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => setEditingPost(post)} className="btn btn-secondary">
                          Edit
                        </button>
                        {!post.is_published && (
                          <button onClick={() => handlePublish(post.id)} className="btn btn-success">
                            Publish
                          </button>
                        )}
                        <button onClick={() => handleDelete(post.id)} className="btn btn-danger">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {(showForm || editingPost) && (
          <div className="modal-overlay" onClick={() => { setShowForm(false); setEditingPost(null); }}>
            <div className="modal-content" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
              <h2>{editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>
              <BlogPostForm
                post={editingPost}
                onSave={editingPost ? (data) => handleUpdate(editingPost.id, data) : handleCreate}
                onCancel={() => { setShowForm(false); setEditingPost(null); }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const BlogPostForm = ({ post, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    featured_image_url: post?.featured_image_url || '',
  });

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (title) => {
    setFormData({
      ...formData,
      title,
      slug: formData.slug || generateSlug(title),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleTitleChange(e.target.value)}
          required
          placeholder="Enter blog post title"
        />
      </div>

      <div className="form-group">
        <label>Slug (URL-friendly) *</label>
        <input
          type="text"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
          required
          placeholder="blog-post-url-slug"
        />
        <small style={{ color: 'var(--secondary-color)' }}>
          URL: /blog/{formData.slug || 'your-slug'}
        </small>
      </div>

      <div className="form-group">
        <label>Featured Image URL</label>
        <input
          type="text"
          value={formData.featured_image_url}
          onChange={(e) => setFormData({ ...formData, featured_image_url: e.target.value })}
          placeholder="/uploads/blog-image.jpg"
        />
        {formData.featured_image_url && (
          <div style={{ marginTop: '10px' }}>
            <img
              src={formData.featured_image_url}
              alt="Preview"
              style={{ maxWidth: '300px', borderRadius: '4px' }}
            />
          </div>
        )}
        <small style={{ color: 'var(--secondary-color)' }}>
          Upload image in Media Library and paste URL here
        </small>
      </div>

      <div className="form-group">
        <label>Excerpt (for SEO and preview)</label>
        <textarea
          value={formData.excerpt}
          onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
          rows="3"
          placeholder="Brief summary of the post (150-160 characters recommended)"
        />
        <small style={{ color: 'var(--secondary-color)' }}>
          {formData.excerpt.length} characters
        </small>
      </div>

      <div className="form-group">
        <label>Content *</label>
        <textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          rows="15"
          required
          placeholder="Write your blog post content here..."
          style={{ fontFamily: 'monospace' }}
        />
        <small style={{ color: 'var(--secondary-color)' }}>
          Supports plain text and basic formatting. Markdown can be added later.
        </small>
      </div>

      <div className="button-group">
        <button type="submit" className="btn btn-primary">
          Save Post
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default BlogManager;
